import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import {
  DesignerApplicationContextActions,
  DesignerApplicationContextState,
} from "../../../hooks/designer-application-context/types";
import {
  DESIGNER_APPLICATION_INITIAL_STATE,
  designerApplicationReducer,
  removeTypeNames,
} from "../../../hooks/designer-application-context/utils";
import { useCreateMembershipApplicationMutation } from "../../../hooks/useCreateMembershipApplicationMutation";
import { useUpdateMembershipApplicationMutation } from "../../../hooks/useUpdateMembershipApplicationMutation";
import { useGetDesignerApplicationQuery } from "../../../hooks/useGetDesignerApplicationQuery";
import { useUserContext } from "../../../hooks/userContext";
import {
  DesignerApplication,
  DesignerSubscriptionType,
  MutationCreateDesignerApplicationArgs,
} from "../../../__generated__/types";
import {
  getMembershipTierFromPlanId,
  getSinglePaymentItemFromId,
  hasApplicationChanged,
} from "../../../utils";
import { useApproveDesigner } from "../../../hooks/use-approve-designer";

const getEmailVerifiedFlag = (value: unknown): boolean | null => {
  const normalized = String(value ?? "").toLowerCase();
  if (normalized === "true") return true;
  if (normalized === "false") return false;
  return null;
};

const DesignerApplicationContext =
  createContext<DesignerApplicationContextState>(
    DESIGNER_APPLICATION_INITIAL_STATE
  );

export const DesignerApplicationContextProvider = ({
  children,
}: PropsWithChildren) => {
  const { user } = useUserContext();

  const [state, setState] = useReducer(
    designerApplicationReducer,
    DESIGNER_APPLICATION_INITIAL_STATE
  );

  const { handleApproveDesigner } = useApproveDesigner();

  const {
    createApplication: createApplicationMutation,
    // error: createApplicationError,
    // loading: createApplicationLoading,
  } = useCreateMembershipApplicationMutation();

  const {
    updateApplication,
    // error: updateApplicationError,
    loading: updateApplicationLoading,
  } = useUpdateMembershipApplicationMutation();

  const {
    getDesignerApplication,
    data: designerApplicationDataFromServer,
    loading: designerApplicationLoading,
  } = useGetDesignerApplicationQuery(user?.id ?? "");

  const saveDesignerApplication = useCallback(
    async (application: Partial<DesignerApplication> = {}) => {
      const update = {
        ...state.application,
        ...application,
        membershipTier: getMembershipTierFromPlanId(
          (state.application as any)?.membershipPlanId ?? ""
        ) as DesignerSubscriptionType,
        singlePaymentItems:
          ((state?.application?.singlePaymentItems as any) ?? [])?.map(
            (item: string) => getSinglePaymentItemFromId(item)
          ) ?? [],
      };
      delete (update as any).__typename;

      const hasChanged = hasApplicationChanged(
        removeTypeNames(designerApplicationDataFromServer, user?.id ?? ""),
        removeTypeNames(update as DesignerApplication, user?.id ?? "")
      );

      delete (update as any).designerId;
      delete (update as any).membershipPlanId; // Remove membershipPlanId as it's not accepted by the schema
      if (designerApplicationDataFromServer) {
        (update as any)._id = (designerApplicationDataFromServer as any)._id;
      }

      if (hasChanged) {
        const result = await updateApplication({
          application: {
            ...update,
            _id: update._id!,
          },
        });
        if (result) return result;
      }
      return state.application;
    },
    [
      state.application,
      updateApplication,
      designerApplicationDataFromServer,
      user?.id,
    ]
  );

  const createApplication = useCallback(
    async (args: MutationCreateDesignerApplicationArgs) => {
      if (!designerApplicationDataFromServer) {
        const result = await createApplicationMutation(args);
        if (result) return result;
      }
    },
    [designerApplicationDataFromServer, createApplicationMutation]
  );

  const refreshApplicationData = useCallback(() => {
    if (user?.id && !designerApplicationLoading) {
      setState({
        type: DesignerApplicationContextActions.UPDATE_APPLICATION,
        payload: { application: null },
      });
      getDesignerApplication();
    }
  }, [user?.id, designerApplicationLoading, getDesignerApplication]);

  const updateApplicationData = useCallback(
    (value: Partial<DesignerApplication>) => {
      setState({
        type: DesignerApplicationContextActions.UPDATE_APPLICATION,
        payload: { application: value },
      });
    },
    []
  );

  const approveDesignerApplication = useCallback(
    async (applicationId: string) => {
      const result = await handleApproveDesigner(applicationId);
      if (!result?.error) {
        return true as const;
      } else {
        return { error: result.error } as const;
      }
    },
    [handleApproveDesigner]
  );

  // NEW: minimal reject (wire up a real mutation later if you have one)
  const rejectDesignerApplication = useCallback(
    async (applicationId: string, _reason?: string) => {
      try {
        // TODO: if you have a real reject mutation/hook, call it here
        // e.g. const res = await handleRejectDesigner(applicationId, _reason);
        // return res?.error ? { error: res.error } : true;
        return true as const;
      } catch (e: any) {
        const msg = e?.message || "Failed to reject application";
        return { error: msg } as const;
      }
    },
    []
  );

  // Fetch the designer application data from the server if it hasn't been fetched yet
  const hasFetchedApplicationData = useRef(false);
  useEffect(() => {
    const emailVerifiedFlag = getEmailVerifiedFlag(user?.emailVerified);
    if (
      !hasFetchedApplicationData.current &&
      user?.id &&
      emailVerifiedFlag === true &&
      !designerApplicationDataFromServer &&
      !designerApplicationLoading
    ) {
      hasFetchedApplicationData.current = true;
      getDesignerApplication();
    }
  }, [
    user?.id,
    user?.emailVerified,
    designerApplicationDataFromServer,
    designerApplicationLoading,
    getDesignerApplication,
  ]);

  // Set the application data from the server once available
  useEffect(() => {
    if (
      !state.application &&
      designerApplicationDataFromServer &&
      !designerApplicationLoading
    ) {
      setState({
        type: DesignerApplicationContextActions.SET_APPLICATION,
        payload: removeTypeNames(
          designerApplicationDataFromServer,
          user?.id ?? ""
        ),
      });
    }
  }, [
    designerApplicationDataFromServer,
    designerApplicationLoading,
    state.application,
    user?.id,
  ]);

  // Clear application if user logs out
  useEffect(() => {
    if (!user && state.application) {
      hasFetchedApplicationData.current = false;
      setState({
        type: DesignerApplicationContextActions.SET_APPLICATION,
        payload: null,
      });
    }
  }, [user, state.application]);

  const value = useMemo(
    () => ({
      ...state,
      loading: updateApplicationLoading || designerApplicationLoading,
      refreshApplicationData,
      updateApplicationData,
      createApplication,
      saveDesignerApplication,
      approveDesignerApplication,
      rejectDesignerApplication, // ⬅️ exposed for consumers
      hasApplicationDataLoaded: !!designerApplicationDataFromServer,
    }),
    [
      state,
      updateApplicationLoading,
      designerApplicationLoading,
      refreshApplicationData,
      updateApplicationData,
      createApplication,
      saveDesignerApplication,
      approveDesignerApplication,
      rejectDesignerApplication,
      designerApplicationDataFromServer,
    ]
  );

  return (
    <DesignerApplicationContext.Provider value={value}>
      {children}
    </DesignerApplicationContext.Provider>
  );
};

export const useDesignerApplicationContext = () => {
  const context = useContext(DesignerApplicationContext);
  if (!context) {
    throw new Error(
      "useDesignerApplicationContext must be used within a DesignerApplicationContextProvider"
    );
  }
  return context;
};

export { MembershipSelectionComponent as MembershipSelection } from './membership-selection-component';
